<template>
  <div class="welcome-view">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">
          Write Your Story.<br />
          <span class="hero-title-highlight">With AI Assistance.</span>
        </h1>
        <p class="hero-subtitle">
          The ultimate writing tool for novelists. Organize your manuscript,
          track plot threads, and get AI-powered writing help—all in one place.
        </p>
        <div class="hero-actions">
          <router-link
            to="/signup"
            class="btn-hero-primary"
          >
            Start Writing Free
          </router-link>
          <router-link
            to="/features"
            class="btn-hero-secondary"
          >
            See Features
          </router-link>
        </div>
      </div>

      <div class="hero-image">
        <div class="hero-image-placeholder">
          <svg width="400" height="300" viewBox="0 0 400 300" fill="none">
            <rect width="400" height="300" rx="12" fill="var(--color-bg-secondary)" stroke="var(--color-border-light)"/>
            <rect x="20" y="20" width="360" height="20" rx="4" fill="var(--color-bg-tertiary)"/>
            <rect x="20" y="50" width="320" height="16" rx="4" fill="var(--color-bg-tertiary)"/>
            <rect x="20" y="76" width="280" height="16" rx="4" fill="var(--color-bg-tertiary)"/>
            <rect x="20" y="120" width="360" height="140" rx="8" fill="var(--color-accent-light)" stroke="var(--color-accent)"/>
            <circle cx="60" cy="160" r="8" fill="var(--color-accent)"/>
            <circle cx="100" cy="160" r="8" fill="var(--color-accent)"/>
            <rect x="40" y="200" width="80" height="20" rx="4" fill="var(--color-accent)"/>
            <rect x="280" y="180" width="80" height="120" rx="4" fill="var(--color-accent-light)" stroke="var(--color-accent)"/>
            <text x="320" y="220" font-family="var(--font-primary)" font-size="12" fill="var(--color-text-secondary)">AI</text>
            <text x="310" y="240" font-family="var(--font-primary)" font-size="12" fill="var(--color-text-secondary)">Assistant</text>
          </svg>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="features-content">
        <h2 class="features-title">
          Everything You Need to Write Your Novel
        </h2>

        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect x="8" y="8" width="32" height="32" rx="4" stroke="var(--color-accent)" stroke-width="2"/>
                <path d="M16 16h16M16 22h16M16 28h12" stroke="var(--color-accent)" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="feature-title">Distraction-Free Editor</h3>
            <p class="feature-description">
              Focus on your writing with our clean, powerful editor powered by TipTap.
            </p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="16" stroke="var(--color-accent)" stroke-width="2"/>
                <path d="M24 12v4M24 32v4M36 24h-4M16 24H12M32.24 15.76l-2.12 2.12M17.88 30.12l-2.12 2.12M32.24 32.24l-2.12-2.12M17.88 17.88l-2.12-2.12" stroke="var(--color-accent)" stroke-width="2" stroke-linecap="round"/>
                <circle cx="24" cy="24" r="6" fill="var(--color-accent-light)"/>
                <circle cx="24" cy="24" r="2" fill="var(--color-accent)"/>
              </svg>
            </div>
            <h3 class="feature-title">AI Writing Assistant</h3>
            <p class="feature-description">
              Get unstuck with intelligent writing suggestions and plot thread tracking.
            </p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="16" cy="16" r="4" stroke="var(--color-accent)" stroke-width="2"/>
                <circle cx="32" cy="16" r="4" stroke="var(--color-accent)" stroke-width="2"/>
                <circle cx="16" cy="32" r="4" stroke="var(--color-accent)" stroke-width="2"/>
                <circle cx="32" cy="32" r="4" stroke="var(--color-accent)" stroke-width="2"/>
                <path d="M20 16h8M20 32h8M16 20v8M32 20v8" stroke="var(--color-accent)" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="feature-title">Plot Thread Tracker</h3>
            <p class="feature-description">
              Never lose track of your narrative threads with intelligent monitoring.
            </p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="16" r="6" stroke="var(--color-accent)" stroke-width="2"/>
                <path d="M12 36c0-4 4-8 12-8s12 4 12 8" stroke="var(--color-accent)" stroke-width="2" stroke-linecap="round"/>
                <circle cx="18" cy="12" r="2" fill="var(--color-accent)"/>
                <circle cx="30" cy="12" r="2" fill="var(--color-accent)"/>
              </svg>
            </div>
            <h3 class="feature-title">Character Management</h3>
            <p class="feature-description">
              Build rich, consistent characters with detailed profiles and relationships.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="cta-content">
        <h2 class="cta-title">
          Ready to Start Writing?
        </h2>
        <p class="cta-subtitle">
          Join thousands of authors already using NovelWriter2.
        </p>
        <router-link
          to="/signup"
          class="btn-cta"
        >
          Get Started Free
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup>
// Welcome view - no complex logic needed
// Router links handle navigation
</script>

<style scoped>
.welcome-view {
  width: 100%;
}

/* Hero Section */
.hero-section {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-3xl) var(--space-2xl) var(--space-2xl);
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-3xl);
  align-items: center;
}

.hero-content {
  text-align: center;
}

.hero-title {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
  margin: 0 0 var(--space-lg) 0;
  color: var(--color-text-primary);
}

.hero-title-highlight {
  color: var(--color-accent);
}

.hero-subtitle {
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-2xl) 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.hero-actions {
  display: flex;
  gap: var(--space-lg);
  justify-content: center;
  flex-wrap: wrap;
}

.btn-hero-primary {
  display: inline-block;
  padding: var(--space-lg) var(--space-2xl);
  background: var(--color-accent);
  color: var(--color-burgundy-dark);
  text-decoration: none;
  border-radius: var(--radius-xl);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-lg);
  transition: all var(--transition-base);
  box-shadow: var(--shadow-lg);
}

.btn-hero-primary:hover {
  background: var(--color-accent-hover);
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(250, 188, 60, 0.3);
}

.btn-hero-secondary {
  display: inline-block;
  padding: var(--space-lg) var(--space-2xl);
  background: transparent;
  color: var(--color-text-primary);
  text-decoration: none;
  border: 2px solid var(--color-border-dark);
  border-radius: var(--radius-xl);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-lg);
  transition: all var(--transition-base);
}

.btn-hero-secondary:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.hero-image {
  display: flex;
  justify-content: center;
}

.hero-image-placeholder {
  max-width: 100%;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-xl);
}

/* Features Section */
.features-section {
  padding: var(--space-3xl) var(--space-2xl);
  background: var(--color-bg-primary);
}

.features-content {
  max-width: 1200px;
  margin: 0 auto;
}

.features-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  text-align: center;
  color: var(--color-text-primary);
  margin: 0 0 var(--space-3xl) 0;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-2xl);
}

.feature-card {
  padding: var(--space-2xl);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border-light);
  transition: all var(--transition-base);
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.feature-icon {
  margin-bottom: var(--space-lg);
}

.feature-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-md) 0;
}

.feature-description {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  line-height: var(--line-height-relaxed);
  margin: 0;
}

/* CTA Section */
.cta-section {
  padding: var(--space-3xl) var(--space-2xl);
  background: linear-gradient(135deg, var(--color-cream) 0%, var(--color-bg-secondary) 100%);
}

.cta-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.cta-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-md) 0;
}

.cta-subtitle {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-2xl) 0;
  line-height: var(--line-height-relaxed);
}

.btn-cta {
  display: inline-block;
  padding: var(--space-lg) var(--space-3xl);
  background: var(--color-accent);
  color: var(--color-burgundy-dark);
  text-decoration: none;
  border-radius: var(--radius-xl);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-lg);
  transition: all var(--transition-base);
  box-shadow: var(--shadow-lg);
}

.btn-cta:hover {
  background: var(--color-accent-hover);
  transform: translateY(-2px);
  box-shadow: 0 16px 32px rgba(250, 188, 60, 0.3);
}

/* Responsive Design */
@media (min-width: 768px) {
  .hero-section {
    grid-template-columns: 1fr 1fr;
    text-align: left;
    padding: var(--space-3xl) var(--space-xl) var(--space-2xl);
  }

  .hero-content {
    text-align: left;
  }

  .hero-title {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
  }

  .hero-subtitle {
    margin-left: 0;
    margin-right: 0;
  }

  .hero-actions {
    justify-content: flex-start;
  }

  .features-section,
  .cta-section {
    padding: var(--space-3xl) var(--space-xl);
  }
}

@media (min-width: 1024px) {
  .hero-section {
    padding: var(--space-3xl) 0 var(--space-2xl);
  }

  .features-section,
  .cta-section {
    padding: var(--space-3xl) 0;
  }
}

@media (max-width: 767px) {
  .hero-section {
    padding: var(--space-2xl) var(--space-lg) var(--space-xl);
  }

  .hero-title {
    font-size: var(--font-size-2xl);
  }

  .hero-subtitle {
    font-size: var(--font-size-base);
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .btn-hero-primary,
  .btn-hero-secondary {
    width: 100%;
    max-width: 300px;
  }

  .features-section,
  .cta-section {
    padding: var(--space-2xl) var(--space-lg);
  }

  .features-title {
    font-size: var(--font-size-xl);
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: var(--space-xl);
  }

  .feature-card {
    padding: var(--space-lg);
  }

  .cta-title {
    font-size: var(--font-size-xl);
  }

  .btn-cta {
    width: 100%;
    max-width: 300px;
  }
}
</style>
